import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { IconChevronLeft, IconChevronRight, IconUsersGroup } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@fragments';

import CommunityMiniCard from '../CommunityMiniCard';

import './styles.scss';

// Cards advanced per navigation press.
const PAGE = 2;

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

/**
 * CommunitiesCarousel — the community rail as a paged carousel: a clipped
 * viewport, a translating track, and prev/next controls that move two cards
 * at a time, always landing with a card flush to the left edge. Card step and
 * visible count are measured from the DOM so the math follows the layout.
 */
export default function CommunitiesCarousel({ communities, onOpen }) {
    const { t } = useTranslation('member');

    const viewportRef = useRef(null);
    const trackRef = useRef(null);
    const [index, setIndex] = useState(0);
    const [metrics, setMetrics] = useState({ step: 0, visible: 1 });

    const measure = useCallback(() => {
        const viewport = viewportRef.current;
        const track = trackRef.current;
        const card = track?.firstElementChild;
        if (!viewport || !track || !card) return;

        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const step = card.offsetWidth + gap;
        const visible = Math.max(1, Math.floor((viewport.clientWidth + gap) / step));
        setMetrics({ step, visible });
    }, []);

    useLayoutEffect(() => {
        measure();
        if (!viewportRef.current || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(viewportRef.current);
        return () => observer.disconnect();
    }, [measure, communities.length]);

    const maxIndex = Math.max(0, communities.length - metrics.visible);
    const current = clamp(index, 0, maxIndex);
    const canPrev = current > 0;
    const canNext = current < maxIndex;
    const overflows = communities.length > metrics.visible;

    const go = (delta) => setIndex(clamp(current + delta, 0, maxIndex));

    return (
        <div className="communities-carousel">
            <div className="communities-carousel__head">
                <Text.h4 weight={500} contrast={9} left={<IconUsersGroup />}>
                    {t('publicProfile.communities.title')}
                </Text.h4>

                {overflows && (
                    <div className="communities-carousel__nav">
                        <Button
                            variant="rounded"
                            color="overlay"
                            size="small"
                            icon={<IconChevronLeft />}
                            disabled={!canPrev}
                            onClick={() => go(-PAGE)}
                            aria-label={t('publicProfile.communities.previous')}
                            preventLoading
                        />
                        <Button
                            variant="rounded"
                            color="overlay"
                            size="small"
                            icon={<IconChevronRight />}
                            disabled={!canNext}
                            onClick={() => go(PAGE)}
                            aria-label={t('publicProfile.communities.next')}
                            preventLoading
                        />
                    </div>
                )}
            </div>

            <div
                className={[
                    'communities-carousel__viewport',
                    canPrev ? 'communities-carousel__viewport--cut-start' : '',
                    canNext ? 'communities-carousel__viewport--cut-end' : '',
                ].filter(Boolean).join(' ')}
                ref={viewportRef}
            >
                <div
                    className="communities-carousel__track"
                    ref={trackRef}
                    style={{ transform: `translateX(${-current * metrics.step}px)` }}
                >
                    {communities.map((c, i) => (
                        <CommunityMiniCard
                            key={c.id || c.slug || i}
                            community={c}
                            index={i}
                            onOpen={onOpen}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
